import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { humanizeSupabaseError } from "@/lib/supabase-errors";

export interface PickerTask {
  id: string;
  title: string;
  parent_task_id: string | null;
  project_column_id: string | null;
  status: string;
  assignee?: { display_name: string | null } | null;
}

export interface PickerTaskOption {
  id: string;
  title: string;
  isSubtask: boolean;
  parentTitle?: string;
  assigneeName?: string | null;
}

const PICKER_SELECT = `
  id, title, parent_task_id, project_column_id, status,
  assignee:assigned_member_id(display_name)
`;

interface UseProjectTasksPickerArgs {
  workspaceId: string | null;
  projectId: string | null;
  columnId?: string | null;
  enabled?: boolean;
}

export function useProjectTasksPicker({
  workspaceId,
  projectId,
  columnId = null,
  enabled = true,
}: UseProjectTasksPickerArgs) {
  const [tasks, setTasks] = useState<PickerTask[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchTasks = useCallback(async () => {
    if (!workspaceId || !projectId || !enabled) {
      setTasks([]);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("workspace_tasks")
        .select(PICKER_SELECT)
        .eq("workspace_id", workspaceId)
        .eq("project_id", projectId)
        .neq("status", "cancelled")
        .order("parent_task_id", { ascending: true, nullsFirst: true })
        .order("title", { ascending: true });

      if (error) throw error;
      setTasks((data ?? []) as unknown as PickerTask[]);
    } catch (err: unknown) {
      console.error("useProjectTasksPicker.fetchTasks:", err);
      toast({
        title: "Erro ao carregar tarefas",
        description: humanizeSupabaseError(err, {
          fallback: "Não foi possível carregar as tarefas. Tente novamente.",
        }),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, projectId, enabled, toast]);

  useEffect(() => {
    void fetchTasks();
  }, [fetchTasks]);

  const options = useMemo((): PickerTaskOption[] => {
    const roots = tasks.filter((t) => t.parent_task_id == null);
    const subtasks = tasks.filter((t) => t.parent_task_id != null);

    const filteredRoots = columnId
      ? roots.filter((t) => t.project_column_id === columnId)
      : roots;

    const rootIds = new Set(filteredRoots.map((r) => r.id));
    const result: PickerTaskOption[] = [];

    for (const root of filteredRoots) {
      result.push({
        id: root.id,
        title: root.title,
        isSubtask: false,
        assigneeName: root.assignee?.display_name,
      });

      const children = subtasks
        .filter((s) => s.parent_task_id === root.id)
        .sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));

      for (const child of children) {
        result.push({
          id: child.id,
          title: child.title,
          isSubtask: true,
          parentTitle: root.title,
          assigneeName: child.assignee?.display_name,
        });
      }
    }

    // Subtarefas cujo pai não está visível (ex.: filtro de coluna) — ainda listar se acessíveis
    for (const child of subtasks) {
      if (rootIds.has(child.parent_task_id!)) continue;
      const parent = tasks.find((t) => t.id === child.parent_task_id);
      if (columnId && parent && parent.project_column_id !== columnId) continue;
      result.push({
        id: child.id,
        title: child.title,
        isSubtask: true,
        parentTitle: parent?.title,
        assigneeName: child.assignee?.display_name,
      });
    }

    return result;
  }, [tasks, columnId]);

  return { tasks, options, loading, refresh: fetchTasks };
}
